import { normalizeString } from './normalize-string'

/** The contact fields a search over clients or participants reads. */
export interface SearchablePerson {
  firstName: string
  lastName: string
  email: string
  phone: string
}

/** Name or e-mail ignoring case and diacritics, or phone ignoring spaces. An empty query matches everyone. */
export function matchesPerson(person: SearchablePerson, query: string): boolean {
  const needle = normalizeString(query).trim()
  if (!needle) {
    return true
  }
  const { firstName, lastName, email, phone } = person
  return (
    normalizeString(`${firstName} ${lastName}`).includes(needle) ||
    normalizeString(email).includes(needle) ||
    phone.replace(/\s/g, '').includes(needle.replace(/\s/g, ''))
  )
}

/** One client a search offers; a `taken` one is shown but cannot be chosen ("už je prihlásený"). */
export interface ClientMatch<T> {
  client: T
  taken: boolean
}

/**
 * What a client search offers for a query (the add-participant dialog): the matching clients in the list's order, at
 * most `limit`. A client already `chosen` is left out; one already `taken` stays in, marked, so the manager sees why
 * they cannot add them.
 */
export function searchClients<T extends SearchablePerson & { id: string }>(
  clients: readonly T[],
  query: string,
  { taken, chosen, limit = 50 }: { taken: ReadonlySet<string>; chosen: ReadonlySet<string>; limit?: number },
): ClientMatch<T>[] {
  const matches: ClientMatch<T>[] = []
  for (const client of clients) {
    if (matches.length >= limit) {
      break
    }
    if (!chosen.has(client.id) && matchesPerson(client, query)) {
      matches.push({ client, taken: taken.has(client.id) })
    }
  }
  return matches
}
